/**
 * usePublicSiteSetting
 *
 * Fetches the bulk `/settings/public` endpoint once per browser session
 * and exposes a single typed value to the caller. Cached on
 * `window.__publicSiteSettings` so multiple consumers on the same page
 * share one request — the property detail page renders the gallery
 * autoplay (this hook's first consumer) but future surfaces (banner
 * messages, default modes, …) shouldn't each pay their own round-trip.
 *
 * Coercion: the backend returns `{ value: string, value_type: 'number'
 * | 'boolean' | 'string' }`. We coerce to the requested generic at the
 * call site via the `parse` argument, with a sensible default fallback
 * if the fetch fails (for resilience — display config should never
 * break the page if the network blips).
 */
import { useEffect, useState } from 'react';
import { apiClient } from '@/shared/api';

type RawSetting = { value: string; value_type: string };
type PublicSettingsResponse = { success: boolean; data: Record<string, RawSetting> };

declare global {
    interface Window {
        __publicSiteSettings?: Promise<Record<string, RawSetting>>;
    }
}

function fetchPublicSettings(): Promise<Record<string, RawSetting>> {
    if (typeof window === 'undefined') {
        return Promise.resolve({});
    }
    if (!window.__publicSiteSettings) {
        window.__publicSiteSettings = apiClient
            .get<PublicSettingsResponse>('/settings/public')
            .then((resp) => resp.data?.data || {})
            .catch(() => ({}));
    }
    return window.__publicSiteSettings;
}

export function usePublicSiteSetting<T>(
    key: string,
    fallback: T,
    parse: (raw: string) => T,
): T {
    const [value, setValue] = useState<T>(fallback);

    useEffect(() => {
        let cancelled = false;
        fetchPublicSettings().then((map) => {
            if (cancelled) return;
            const raw = map[key];
            if (!raw || !raw.value) return;
            try {
                setValue(parse(raw.value));
            } catch {
                // Bad value in the DB shouldn't break the page; just fall
                // through to the hardcoded fallback already in state.
            }
        });
        return () => {
            cancelled = true;
        };
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

    return value;
}

/**
 * Convenience wrapper for numeric settings — the most common shape
 * for display tunables (intervals, limits, etc.).
 */
export function usePublicNumberSetting(key: string, fallback: number): number {
    return usePublicSiteSetting<number>(key, fallback, (raw) => {
        const n = Number(raw);
        return Number.isFinite(n) ? n : fallback;
    });
}
